import type { CheckboxGroupWithTextInputProps } from '@ksyos/design-system/dist/components/checkbox-group-with-text-input';
import type { CheckboxGroupWithTextInputData } from '@ksyos/design-system/dist/components/checkbox-group-with-text-input/create-checkbox-group-options';
import type { CheckboxOption } from '@common/validation/customControls/nestedCheckboxGroupWithTextInput';
import type { OmittedFormGroupFieldProps } from '@frontend/components/fields/FormGroup';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';

export type CheckboxData = Record<string, string | null>;

export type CheckboxGroupWithTextInputFormGroupFieldProps = OmittedFormGroupFieldProps &
    CheckboxGroupWithTextInputProps;

export type NestedCheckboxGroupWithTextInputFormGroupFieldProps = OmittedFormGroupFieldProps & {
    data: { [key: string]: boolean | string | null };
    error?: string;
    onChange: (key: string, value: boolean | string | null) => void;
    options: CheckboxOption[];
};

export function computeCheckboxGroupWithTextInputProps({
    data,
    error,
    onChange,
}: CheckboxGroupWithTextInputProps): CheckboxGroupWithTextInputProps {
    return {
        data,
        error: getValidationErrorTranslation(error),
        onChange,
    };
}

// This function is specific for the form engine. It is being investigated whether this can be removed
export function orderEnumValues(enumType: { [p: string]: string }, orderedKeys: string[]): Record<string, string> {
    const valueToKeyMap = Object.fromEntries(Object.entries(enumType).map(([key, val]) => [val, key]));

    return orderedKeys.reduce(
        (acc, key) => {
            if (valueToKeyMap[key]) {
                acc[valueToKeyMap[key]] = key;
            }

            return acc;
        },
        {} as Record<string, string>
    );
}

export function createCheckboxGroupWithTextInputData(
    data: CheckboxData,
    labels: Record<string, string>,
    hasTextInputOptions: string[] = []
): CheckboxGroupWithTextInputData {
    return Object.entries(labels).reduce(
        (acc, [key, label]) => ({
            ...acc,
            [key]: {
                displayValue: label,
                hasTextInput: key === 'anders' || hasTextInputOptions.includes(key),
                value: data[key] ?? undefined,
            },
        }),
        {} as CheckboxGroupWithTextInputData
    );
}

export function convertCheckboxGroupWithTextInputDataToCheckboxData(
    data: CheckboxGroupWithTextInputData
): CheckboxData {
    return Object.entries(data).reduce(
        (acc, [key, { value }]) => ({
            ...acc,
            [key]: value || null,
        }),
        {}
    );
}
